import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import Icon from 'components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { useRealtimeAdminMetrics } from '../../../hooks/useRealtimeAdminMetrics';
import { getCacheHealth } from '../../../utils/redisCache';
import RealtimeStatusBadge from './RealtimeStatusBadge';

const SEVERITY_COLORS = {
  BOT_LIKE_INTERVAL: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  AUTOMATED_TRADING: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  MULTI_ACCOUNT_CLUSTER: 'bg-red-500/10 text-red-400 border-red-500/20',
  bot_precision: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  multi_account: 'bg-red-500/10 text-red-400 border-red-500/20',
};

const getRiskColor = (score) => {
  if (score < 10) return '#22c55e';
  if (score < 50) return '#f59e0b';
  if (score < 100) return '#f97316';
  return '#ef4444';
};

const UserDetailModal = ({ user, onClose }) => (
  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
    <div className="card w-full max-w-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-bold text-foreground">User Details</h3>
        <button onClick={onClose} className="p-1.5 hover:bg-surface rounded-lg"><Icon name="X" size={16} /></button>
      </div>
      <div className="space-y-3">
        <div className="flex justify-between"><span className="text-sm text-muted-foreground">Username</span><span className="text-sm font-medium text-foreground">{user?.username ?? 'Unknown'}</span></div>
        <div className="flex justify-between"><span className="text-sm text-muted-foreground">Trade Frequency</span><span className="text-sm font-mono text-foreground">{user?.tradeFreq?.toFixed(2) ?? 0}/hr</span></div>
        <div className="flex justify-between"><span className="text-sm text-muted-foreground">Crime Frequency</span><span className="text-sm font-mono text-foreground">{user?.crimeFreq?.toFixed(2) ?? 0}/hr</span></div>
        <div className="flex justify-between"><span className="text-sm text-muted-foreground">Risk Score</span><span className="text-sm font-mono font-bold" style={{ color: getRiskColor(user?.riskScore ?? 0) }}>{user?.riskScore?.toFixed(0) ?? 0}</span></div>
      </div>
      <button onClick={onClose} className="mt-5 w-full py-2 rounded-lg bg-surface border border-border text-sm text-muted-foreground hover:text-foreground">Close</button>
    </div>
  </div>
);

const SimulationMetrics = ({ liveMetrics, connectionStatus }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simLoading, setSimLoading] = useState(false);

  useEffect(() => {
    const fetchInitial = async () => {
      const { data } = await supabase?.from('simulation_metrics')?.select('*')?.order('updated_at', { ascending: false })?.limit(1)?.maybeSingle();
      setMetrics(data);
      setLoading(false);
    };
    fetchInitial();
  }, []);

  useEffect(() => { if (liveMetrics) setMetrics(liveMetrics); }, [liveMetrics]);

  const handleStartSim = async () => {
    setSimLoading(true);
    try {
      if (metrics?.id) {
        await supabase?.from('simulation_metrics')?.update({
          is_running: true,
          active_bots: 50000,
          actions_per_second: Math.random() * 5000 + 1000,
          api_latency_ms: Math.random() * 50 + 10,
          memory_usage_mb: Math.random() * 2000 + 500,
          tick_impact: Math.random() * 0.5,
          started_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString(),
        })?.eq('id', metrics?.id);
      }
    } finally { setSimLoading(false); }
  };

  const handleStopSim = async () => {
    setSimLoading(true);
    try {
      if (metrics?.id) {
        await supabase?.from('simulation_metrics')?.update({
          is_running: false,
          active_bots: 0,
          actions_per_second: 0,
          stopped_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString(),
        })?.eq('id', metrics?.id);
      }
    } finally { setSimLoading(false); }
  };

  if (loading) return <div className="card p-5"><Icon name="Loader" size={20} className="animate-spin text-primary" /></div>;

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase">Live Bot Simulation Metrics</h3>
          {metrics?.is_running && (<span className="flex items-center gap-1 text-xs text-green-400"><span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />RUNNING</span>)}
          <div className="flex items-center gap-1">
            <span className={`w-1.5 h-1.5 rounded-full ${connectionStatus === 'connected' ? 'bg-green-400 animate-pulse' : connectionStatus === 'connecting' ? 'bg-yellow-400 animate-pulse' : 'bg-red-400'}`} />
            <span className="text-xs text-muted-foreground">{connectionStatus === 'connected' ? 'Live' : connectionStatus === 'connecting' ? 'Connecting...' : 'Disconnected'}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={handleStartSim} disabled={simLoading || metrics?.is_running} className="px-3 py-1.5 rounded-lg bg-green-500/10 text-green-400 border border-green-500/20 text-xs font-semibold hover:bg-green-500/20 disabled:opacity-40">{simLoading ? '...' : 'Start Simulation'}</button>
          <button onClick={handleStopSim} disabled={simLoading || !metrics?.is_running} className="px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 text-xs font-semibold hover:bg-red-500/20 disabled:opacity-40">{simLoading ? '...' : 'Stop Simulation'}</button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Active Bots', value: (metrics?.active_bots ?? 0)?.toLocaleString(), icon: 'Bot', color: 'text-primary' },
          { label: 'Actions/sec', value: Number(metrics?.actions_per_second ?? 0)?.toFixed(0), icon: 'Zap', color: 'text-yellow-400' },
          { label: 'API Latency', value: Number(metrics?.api_latency_ms ?? 0)?.toFixed(1) + 'ms', icon: 'Clock', color: 'text-blue-400' },
          { label: 'Memory', value: Number(metrics?.memory_usage_mb ?? 0)?.toFixed(0) + 'MB', icon: 'Cpu', color: 'text-purple-400' },
          { label: 'Tick Impact', value: Number(metrics?.tick_impact ?? 0)?.toFixed(4), icon: 'Activity', color: 'text-orange-400' },
        ]?.map((m) => (
          <div key={m?.label} className="bg-surface rounded-lg p-3 border border-border">
            <div className="flex items-center gap-1.5 mb-1"><Icon name={m?.icon} size={12} className={m?.color} /><span className="text-xs text-muted-foreground">{m?.label}</span></div>
            <p className={`text-lg font-mono font-bold ${m?.color}`}>{m?.value}</p>
          </div>
        ))}
      </div>
      {metrics?.started_at && (<p className="text-xs text-muted-foreground mt-3">{metrics?.is_running ? 'Started' : 'Last run'}: {new Date(metrics.started_at)?.toLocaleString()}</p>)}
    </div>
  );
};

const ActiveUsersChart = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const since = new Date(Date.now() - 24 * 3600000)?.toISOString();
        const { data: logs } = await supabase?.from('audit_logs')?.select('user_id, created_at')?.gte('created_at', since)?.order('created_at', { ascending: true });
        const buckets = {};
        const now = Date.now();
        for (let i = 0; i < 288; i++) {
          const t = new Date(now - (287 - i) * 5 * 60000);
          const key = t?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          buckets[key] = new Set();
        }
        (logs ?? [])?.forEach((log) => {
          const t = new Date(log.created_at);
          const rounded = new Date(Math.floor(t.getTime() / (5 * 60000)) * (5 * 60000));
          const key = rounded?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          if (buckets?.[key] && log?.user_id) buckets?.[key]?.add(log?.user_id);
        });
        setData(Object.entries(buckets)?.map(([time, users]) => ({ time, users: users?.size }))?.slice(-48));
      } catch (err) { console.error('ActiveUsers fetch error:', err); } finally { setLoading(false); }
    };
    fetchData();
    const t = setInterval(fetchData, 300000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase">Active Users (Last 4h · 5min buckets)</h3>
        <span className="text-xs text-muted-foreground">From audit_logs</span>
      </div>
      {loading ? (<div className="flex items-center justify-center h-32"><Icon name="Loader" size={20} className="animate-spin text-primary" /></div>) : (
        <ResponsiveContainer width="100%" height={160}>
          <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis
              dataKey="time"
              tick={{ fontSize: 9, fill: 'var(--color-muted-foreground)' }}
              tickLine={false}
              interval={11}
            />
            <YAxis
              tick={{ fontSize: 9, fill: 'var(--color-muted-foreground)' }}
              tickLine={false}
              width={30}
            />
            <Tooltip
              contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 8 }}
              labelStyle={{ color: 'var(--color-muted-foreground)', fontSize: 11 }}
              itemStyle={{ color: 'var(--color-primary)', fontSize: 12 }}
            />
            <Line type="monotone" dataKey="users" stroke="var(--color-primary)" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

const TradeFrequencyAnomalies = () => {
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const since = new Date(Date.now() - 3600000)?.toISOString();
        const { data: trades } = await supabase?.from('trades')?.select('user_id, created_at, user_profiles(username)')?.gte('created_at', since);
        const userMap = {};
        (trades ?? [])?.forEach((t) => { if (!t?.user_id) return; if (!userMap?.[t?.user_id]) userMap[t.user_id] = { user_id: t?.user_id, username: t?.user_profiles?.username ?? 'Unknown', count: 0 }; userMap[t.user_id].count += 1; });
        const counts = Object.values(userMap)?.map((u) => u?.count);
        if (counts?.length === 0) { setAnomalies([]); setLoading(false); return; }
        const mean = counts?.reduce((a, b) => a + b, 0) / counts?.length;
        const variance = counts?.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / counts?.length;
        const stddev = Math.sqrt(variance);
        setAnomalies(Object.values(userMap)?.filter((u) => u?.count > mean + 2 * stddev)?.map((u) => ({ ...u, tradesPerHour: u?.count, baseline: mean?.toFixed(1), anomalyScore: stddev > 0 ? ((u?.count - mean) / stddev)?.toFixed(2) : '0' }))?.sort((a, b) => b?.tradesPerHour - a?.tradesPerHour)?.slice(0, 20));
      } catch (err) { console.error('TradeAnomalies fetch error:', err); } finally { setLoading(false); }
    };
    fetchData();
    const t = setInterval(fetchData, 60000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4"><h3 className="text-sm font-semibold text-muted-foreground uppercase">Trade Frequency Anomalies</h3><span className="text-xs text-muted-foreground">Mean + 2σ threshold · Last 1h</span></div>
      {loading ? (<div className="flex items-center justify-center py-6"><Icon name="Loader" size={20} className="animate-spin text-primary" /></div>) : anomalies?.length === 0 ? (<p className="text-sm text-muted-foreground text-center py-6">No trade frequency anomalies detected</p>) : (
        <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b border-border"><th className="text-left py-2 px-2 text-xs text-muted-foreground uppercase">Username</th><th className="text-right py-2 px-2 text-xs text-muted-foreground uppercase">Trades/hr</th><th className="text-right py-2 px-2 text-xs text-muted-foreground uppercase">Baseline</th><th className="text-right py-2 px-2 text-xs text-muted-foreground uppercase">Anomaly Score</th></tr></thead><tbody>{anomalies?.map((a) => (<tr key={a?.user_id} className="border-b border-border/30 hover:bg-surface/50"><td className="py-2 px-2 font-medium text-foreground">{a?.username}</td><td className="py-2 px-2 text-right font-mono text-orange-400 font-bold">{a?.tradesPerHour}</td><td className="py-2 px-2 text-right font-mono text-muted-foreground">{a?.baseline}</td><td className="py-2 px-2 text-right"><span className={`font-mono font-bold text-xs px-2 py-0.5 rounded border ${Number(a?.anomalyScore) >= 4 ? 'bg-red-500/10 text-red-400 border-red-500/20' : Number(a?.anomalyScore) >= 2 ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'}`}>{a?.anomalyScore}σ</span></td></tr>))}</tbody></table></div>
      )}
    </div>
  );
};

const CrimeFrequencyAnomalies = () => {
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const since = new Date(Date.now() - 3600000)?.toISOString();
        const { data: crimes } = await supabase?.from('crime_attempts')?.select('user_id, created_at, user_profiles(username)')?.gte('created_at', since);
        const userMap = {};
        (crimes ?? [])?.forEach((c) => { if (!c?.user_id) return; if (!userMap?.[c?.user_id]) userMap[c.user_id] = { user_id: c?.user_id, username: c?.user_profiles?.username ?? 'Unknown', count: 0 }; userMap[c.user_id].count += 1; });
        const counts = Object.values(userMap)?.map((u) => u?.count);
        if (counts?.length === 0) { setAnomalies([]); setLoading(false); return; }
        const mean = counts?.reduce((a, b) => a + b, 0) / counts?.length;
        const variance = counts?.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / counts?.length;
        const stddev = Math.sqrt(variance);
        setAnomalies(Object.values(userMap)?.filter((u) => u?.count > mean + 2 * stddev)?.map((u) => ({ ...u, crimesPerHour: u?.count, baseline: mean?.toFixed(1), anomalyScore: stddev > 0 ? ((u?.count - mean) / stddev)?.toFixed(2) : '0' }))?.sort((a, b) => b?.crimesPerHour - a?.crimesPerHour)?.slice(0, 20));
      } catch (err) { console.error('CrimeAnomalies fetch error:', err); } finally { setLoading(false); }
    };
    fetchData();
    const t = setInterval(fetchData, 60000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4"><h3 className="text-sm font-semibold text-muted-foreground uppercase">Crime Frequency Anomalies</h3><span className="text-xs text-muted-foreground">Bot-like crime intervals · Last 1h</span></div>
      {loading ? (<div className="flex items-center justify-center py-6"><Icon name="Loader" size={20} className="animate-spin text-primary" /></div>) : anomalies?.length === 0 ? (<p className="text-sm text-muted-foreground text-center py-6">No crime frequency anomalies detected</p>) : (
        <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b border-border"><th className="text-left py-2 px-2 text-xs text-muted-foreground uppercase">Username</th><th className="text-right py-2 px-2 text-xs text-muted-foreground uppercase">Crimes/hr</th><th className="text-right py-2 px-2 text-xs text-muted-foreground uppercase">Baseline</th><th className="text-right py-2 px-2 text-xs text-muted-foreground uppercase">Anomaly Score</th></tr></thead><tbody>{anomalies?.map((a) => (<tr key={a?.user_id} className="border-b border-border/30 hover:bg-surface/50"><td className="py-2 px-2 font-medium text-foreground">{a?.username}</td><td className="py-2 px-2 text-right font-mono text-purple-400 font-bold">{a?.crimesPerHour}</td><td className="py-2 px-2 text-right font-mono text-muted-foreground">{a?.baseline}</td><td className="py-2 px-2 text-right"><span className={`font-mono font-bold text-xs px-2 py-0.5 rounded border ${Number(a?.anomalyScore) >= 4 ? 'bg-red-500/10 text-red-400 border-red-500/20' : Number(a?.anomalyScore) >= 2 ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'}`}>{a?.anomalyScore}σ</span></td></tr>))}</tbody></table></div>
      )}
    </div>
  );
};

const BotDetectionAlerts = ({ onInvestigate, liveAlerts, newAlertIds, clearNewAlert }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchInitial = async () => {
      const { data } = await supabase?.from('suspicious_activity')?.select('*, user_profiles(username)')?.in('flag_type', ['bot_precision', 'multi_account', 'BOT_LIKE_INTERVAL', 'AUTOMATED_TRADING', 'MULTI_ACCOUNT_CLUSTER'])?.order('created_at', { ascending: false })?.limit(30);
      setAlerts(data ?? []);
      setLoading(false);
    };
    fetchInitial();
  }, []);
  useEffect(() => {
    if (!liveAlerts?.length) return;
    const botTypes = ['bot_precision', 'multi_account', 'BOT_LIKE_INTERVAL', 'AUTOMATED_TRADING', 'MULTI_ACCOUNT_CLUSTER'];
    const newBotAlerts = liveAlerts?.filter(a => botTypes?.includes(a?.flag_type));
    if (newBotAlerts?.length > 0) {
      setAlerts(prev => { const existingIds = new Set(prev.map(a => a?.id)); const fresh = newBotAlerts?.filter(a => !existingIds?.has(a?.id)); return [...fresh, ...prev]?.slice(0, 30); });
    }
  }, [liveAlerts]);
  const getSeverity = (score) => {
    if (score >= 80) return { label: 'CRITICAL', cls: 'bg-red-500/10 text-red-400 border-red-500/20' };
    if (score >= 60) return { label: 'HIGH', cls: 'bg-orange-500/10 text-orange-400 border-orange-500/20' };
    if (score >= 30) return { label: 'MEDIUM', cls: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' };
    return { label: 'LOW', cls: 'bg-blue-500/10 text-blue-400 border-blue-500/20' };
  };
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><h3 className="text-sm font-semibold text-muted-foreground uppercase">Bot Detection Alerts</h3><span className="flex items-center gap-1 text-xs text-green-400"><span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />Live</span></div>
        <span className="text-xs text-muted-foreground">{alerts?.length} alerts</span>
      </div>
      {loading ? (<div className="flex items-center justify-center py-6"><Icon name="Loader" size={20} className="animate-spin text-primary" /></div>) : alerts?.length === 0 ? (<p className="text-sm text-muted-foreground text-center py-6">No bot detection alerts</p>) : (
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {alerts?.map((alert) => {
            const sev = getSeverity(Number(alert?.risk_score ?? 0));
            const isNew = newAlertIds?.has(alert?.id);
            return (
              <div key={alert?.id} className={`flex items-center justify-between p-3 rounded-lg border transition-all duration-300 ${SEVERITY_COLORS?.[alert?.flag_type] ?? 'bg-surface border-border'} ${isNew ? 'ring-1 ring-primary/30' : ''}`} style={isNew ? { animation: 'slideInRight 0.3s ease-out' } : {}} onAnimationEnd={() => clearNewAlert?.(alert?.id)}>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground">{alert?.user_profiles?.username ?? 'Unknown'}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded border ${sev?.cls}`}>{sev?.label}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded border ${SEVERITY_COLORS?.[alert?.flag_type] ?? 'bg-surface border-border'}`}>{alert?.flag_type?.replace(/_/g, ' ')}</span>
                    {isNew && <span className="text-xs px-1.5 py-0.5 rounded bg-primary/20 text-primary border border-primary/30 font-semibold">NEW</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">Risk: <span className="font-mono text-foreground">{Number(alert?.risk_score ?? 0)?.toFixed(0)}</span> &middot; {new Date(alert?.created_at)?.toLocaleTimeString()}</p>
                </div>
                <button onClick={() => onInvestigate?.(alert)} className="px-3 py-1.5 rounded-lg bg-primary/10 text-primary border border-primary/20 text-xs font-semibold hover:bg-primary/20">Investigate</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const CheatDetectionAlerts = ({ liveCheatAlerts, newAlertIds, clearNewAlert }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchInitial = async () => {
      const { data } = await supabase?.from('suspicious_activity')?.select('*, user_profiles(username)')?.not('flag_type', 'in', '(bot_precision,multi_account,BOT_LIKE_INTERVAL,AUTOMATED_TRADING,MULTI_ACCOUNT_CLUSTER)')?.order('created_at', { ascending: false })?.limit(20);
      setAlerts(data ?? []);
      setLoading(false);
    };
    fetchInitial();
  }, []);
  useEffect(() => {
    if (!liveCheatAlerts?.length) return;
    setAlerts(prev => { const existingIds = new Set(prev.map(a => a?.id)); const fresh = liveCheatAlerts?.filter(a => !existingIds?.has(a?.id)); return [...fresh, ...prev]?.slice(0, 20); });
  }, [liveCheatAlerts]);
  if (loading) return <div className="card p-5"><Icon name="Loader" size={20} className="animate-spin text-primary" /></div>;
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><h3 className="text-sm font-semibold text-muted-foreground uppercase">Cheat Detection Alerts</h3><span className="flex items-center gap-1 text-xs text-green-400"><span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />Live</span></div>
        <span className="text-xs text-muted-foreground">{alerts?.length} alerts</span>
      </div>
      {alerts?.length === 0 ? (<p className="text-sm text-muted-foreground text-center py-6">No cheat detection alerts</p>) : (
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {alerts?.map((alert) => {
            const isNew = newAlertIds?.has(alert?.id);
            return (
              <div key={alert?.id} className={`p-3 rounded-lg border transition-all duration-300 bg-surface border-border ${isNew ? 'ring-1 ring-warning/40' : ''}`} style={isNew ? { animation: 'slideInRight 0.3s ease-out' } : {}} onAnimationEnd={() => clearNewAlert?.(alert?.id)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">{alert?.user_profiles?.username ?? 'Unknown'}</span>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-warning/10 text-warning border border-warning/20 uppercase">{alert?.flag_type?.replace(/_/g, ' ')}</span>
                    {isNew && <span className="text-xs px-1.5 py-0.5 rounded bg-primary/20 text-primary border border-primary/30 font-semibold">NEW</span>}
                  </div>
                  <span className="text-xs text-muted-foreground">{new Date(alert?.created_at)?.toLocaleTimeString()}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Risk Score: <span className="font-mono text-foreground">{Number(alert?.risk_score ?? 0)?.toFixed(0)}</span></p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ClusterVisualization = ({ onUserClick }) => {
  const [clusterData, setClusterData] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const since = new Date(Date.now() - 3600000)?.toISOString();
        const [tradesRes, crimesRes, riskRes] = await Promise.all([supabase?.from('trades')?.select('user_id')?.gte('created_at', since), supabase?.from('crime_attempts')?.select('user_id')?.gte('created_at', since), supabase?.from('suspicious_activity')?.select('user_id, risk_score')?.order('risk_score', { ascending: false })?.limit(200)]);
        const tradeCount = {}; (tradesRes?.data ?? [])?.forEach((t) => { if (t?.user_id) tradeCount[t.user_id] = (tradeCount?.[t?.user_id] ?? 0) + 1; });
        const crimeCount = {}; (crimesRes?.data ?? [])?.forEach((c) => { if (c?.user_id) crimeCount[c.user_id] = (crimeCount?.[c?.user_id] ?? 0) + 1; });
        const riskMap = {}; (riskRes?.data ?? [])?.forEach((r) => { if (r?.user_id) riskMap[r.user_id] = Math.max(riskMap?.[r?.user_id] ?? 0, Number(r?.risk_score ?? 0)); });
        const allUsers = new Set([...Object.keys(tradeCount), ...Object.keys(crimeCount), ...Object.keys(riskMap)]);
        setClusterData(Array.from(allUsers)?.map((uid) => ({ userId: uid, username: uid?.slice(0, 8), tradeFreq: tradeCount?.[uid] ?? 0, crimeFreq: crimeCount?.[uid] ?? 0, riskScore: riskMap?.[uid] ?? 0, fill: getRiskColor(riskMap?.[uid] ?? 0) })));
      } catch (err) { console.error('ClusterViz fetch error:', err); } finally { setLoading(false); }
    };
    fetchData();
    const t = setInterval(fetchData, 60000);
    return () => clearInterval(t);
  }, []);
  const CustomDot = (props) => { const { cx, cy, payload } = props; return (<circle cx={cx} cy={cy} r={5} fill={payload?.fill ?? '#22c55e'} fillOpacity={0.8} stroke={payload?.fill ?? '#22c55e'} strokeWidth={1} style={{ cursor: 'pointer' }} onClick={() => onUserClick?.(payload)} />); };
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4"><h3 className="text-sm font-semibold text-muted-foreground uppercase">Cluster Visualization</h3><div className="flex items-center gap-3 text-xs"><span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400" />&lt;10</span><span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400" />10-50</span><span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400" />50-100</span><span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400" />&gt;100</span></div></div>
      <p className="text-xs text-muted-foreground mb-3">Trade Frequency (X) vs Crime Frequency (Y) · Click point for details</p>
      {loading ? (<div className="flex items-center justify-center h-48"><Icon name="Loader" size={20} className="animate-spin text-primary" /></div>) : clusterData?.length === 0 ? (<p className="text-sm text-muted-foreground text-center py-12">No cluster data available</p>) : (
        <ResponsiveContainer width="100%" height={280}>
          <ScatterChart margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="tradeFreq" name="Trade Freq" tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }} tickLine={false} label={{ value: 'Trade Freq/hr', position: 'insideBottom', offset: -5, fontSize: 10, fill: 'var(--color-muted-foreground)' }} />
            <YAxis dataKey="crimeFreq" name="Crime Freq" tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }} tickLine={false} width={35} label={{ value: 'Crime/hr', angle: -90, position: 'insideLeft', fontSize: 10, fill: 'var(--color-muted-foreground)' }} />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} content={({ active, payload }) => { if (!active || !payload?.length) return null; const d = payload?.[0]?.payload; return (<div className="bg-card border border-border rounded-lg p-3 shadow-lg"><p className="text-xs font-mono text-foreground">{d?.username}</p><p className="text-xs text-muted-foreground">Trades: {d?.tradeFreq}</p><p className="text-xs text-muted-foreground">Crimes: {d?.crimeFreq}</p><p className="text-xs" style={{ color: d?.fill }}>Risk: {d?.riskScore?.toFixed(0)}</p></div>); }} />
            <Scatter data={clusterData} shape={<CustomDot />} />
          </ScatterChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

const CacheStatsPanel = ({ cacheHealth }) => {
  if (!cacheHealth) return null;
  const { stats, mode, connected } = cacheHealth;
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase">Cache Statistics</h3>
          <div className="flex items-center gap-1">
            <span className={`w-1.5 h-1.5 rounded-full ${mode === 'redis' && connected ? 'bg-green-400 animate-pulse' : 'bg-blue-400'}`} />
            <span className="text-xs text-muted-foreground">{mode === 'redis' ? (connected ? 'Redis Connected' : 'Redis Offline') : 'In-Memory Cache'}</span>
          </div>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded border font-semibold ${stats?.hitRate >= 80 ? 'bg-success/10 text-success border-success/20' : stats?.hitRate >= 50 ? 'bg-warning/10 text-warning border-warning/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>{stats?.hitRate}% Hit Rate {stats?.hitRate >= 80 ? '\u2713' : stats?.hitRate >= 50 ? '~' : '\u2193'}</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[
          { label: 'Cache Hits', value: stats?.hits?.toLocaleString(), icon: 'CheckCircle', color: 'text-success' },
          { label: 'Cache Misses', value: stats?.misses?.toLocaleString(), icon: 'XCircle', color: 'text-red-400' },
          { label: 'Evictions', value: stats?.evictions?.toLocaleString(), icon: 'Trash2', color: 'text-warning' },
          { label: 'Cache Size', value: stats?.cacheSize + ' keys', icon: 'Database', color: 'text-primary' },
        ]?.map((m) => (<div key={m?.label} className="bg-surface rounded-lg p-3 border border-border"><div className="flex items-center gap-1.5 mb-1"><Icon name={m?.icon} size={12} className={m?.color} /><span className="text-xs text-muted-foreground">{m?.label}</span></div><p className={`text-base font-mono font-bold ${m?.color}`}>{m?.value}</p></div>))}
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-surface rounded-lg p-3 border border-border"><p className="text-xs text-muted-foreground mb-1">Avg Query Time (Cached)</p><p className="text-base font-mono font-bold text-success">{stats?.avgQueryTimeWithCache}ms</p></div>
        <div className="bg-surface rounded-lg p-3 border border-border"><p className="text-xs text-muted-foreground mb-1">Avg Query Time (DB)</p><p className="text-base font-mono font-bold text-warning">{stats?.avgQueryTimeWithoutCache}ms</p></div>
      </div>
      {stats?.topKeys?.length > 0 && (<div><p className="text-xs text-muted-foreground uppercase mb-2">Most Accessed Keys</p><div className="space-y-1">{stats?.topKeys?.map((k) => (<div key={k?.key} className="flex items-center justify-between text-xs py-1 border-b border-border/30"><span className="font-mono text-foreground">{k?.key}</span><span className="text-muted-foreground">{k?.count} accesses</span></div>))}</div></div>)}
    </div>
  );
};

const BotDetectionTab = () => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [cacheHealth, setCacheHealth] = useState(null);
  const [latencyMs, setLatencyMs] = useState(null);
  const latencyHistoryRef = useRef([]);
  const retryCounts = useRef({ botDetection: 0, simMetrics: 0, cheatDetection: 0 });

  const { botAlerts, simMetrics, cheatAlerts, channelStatuses, connectionStatus, allConnected, newAlertIds, clearNewAlert } = useRealtimeAdminMetrics({ enabled: true });

  // Measure latency: track time between subscription events
  useEffect(() => {
    if (!botAlerts?.length) return;
    const now = Date.now();
    const latest = botAlerts?.[0];
    if (latest?.created_at) {
      const eventTime = new Date(latest.created_at)?.getTime();
      const latency = Math.max(0, now - eventTime);
      latencyHistoryRef.current = [...latencyHistoryRef?.current, latency]?.slice(-10);
      const avg = Math.round(
        latencyHistoryRef?.current?.reduce((a, b) => a + b, 0) / latencyHistoryRef?.current?.length
      );
      setLatencyMs(avg);
    }
  }, [botAlerts]);

  // Track retry counts from channelStatuses changes
  useEffect(() => {
    Object.entries(channelStatuses || {})?.forEach(([key, status]) => {
      if (status === 'error') {
        retryCounts.current[key] = (retryCounts?.current?.[key] || 0) + 1;
      } else if (status === 'connected') {
        retryCounts.current[key] = 0;
      }
    });
  }, [channelStatuses]);

  const handleReconnectNow = useCallback(() => {
    // Force page reload of subscriptions by toggling - simplest approach
    window.location?.reload();
  }, []);

  useEffect(() => {
    const loadCacheHealth = () => { try { setCacheHealth(getCacheHealth()); } catch (e) { console.warn('[BotDetectionTab] Cache health error:', e); } };
    loadCacheHealth();
    const t = setInterval(loadCacheHealth, 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-5">
      {/* Enhanced Realtime Status Badge */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase">Real-Time Subscription Status</h3>
        </div>
        <RealtimeStatusBadge
          channelStatuses={channelStatuses}
          connectionStatus={connectionStatus}
          allConnected={allConnected}
          retryCounts={retryCounts?.current}
          latencyMs={latencyMs}
          onReconnectNow={handleReconnectNow}
        />
      </div>
      <SimulationMetrics liveMetrics={simMetrics} connectionStatus={connectionStatus} />
      <CacheStatsPanel cacheHealth={cacheHealth} />
      <ActiveUsersChart />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4"><TradeFrequencyAnomalies /><CrimeFrequencyAnomalies /></div>
      <BotDetectionAlerts onInvestigate={(alert) => setSelectedUser({ userId: alert?.user_id, username: alert?.user_profiles?.username ?? 'Unknown', tradeFreq: 0, crimeFreq: 0, riskScore: Number(alert?.risk_score ?? 0) })} liveAlerts={botAlerts} newAlertIds={newAlertIds} clearNewAlert={clearNewAlert} />
      <CheatDetectionAlerts liveCheatAlerts={cheatAlerts} newAlertIds={newAlertIds} clearNewAlert={clearNewAlert} />
      <ClusterVisualization onUserClick={(user) => setSelectedUser(user)} />
      {selectedUser && (<UserDetailModal user={selectedUser} onClose={() => setSelectedUser(null)} />)}
    </div>
  );
};

export default BotDetectionTab;
